import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';

const FloatingActionButton = ({ onClick, icon: Icon, label }) => {
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const hasMoved = useRef(false);

  // Load saved position from localStorage
  useEffect(() => {
    try {
      const savedPosition = localStorage.getItem('fab-position');
      if (savedPosition) {
        setPosition(JSON.parse(savedPosition));
      }
    } catch {}
  }, []);

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const rect = buttonRef.current.getBoundingClientRect();
      setDragStart({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });
      setIsDragging(true);
      hasMoved.current = false;
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;
      
      // Constrain to viewport
      const maxX = window.innerWidth - buttonRef.current.offsetWidth;
      const maxY = window.innerHeight - buttonRef.current.offsetHeight;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
      hasMoved.current = true;
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      
      // Save position if moved
      if (hasMoved.current && position.x !== null) {
        try { localStorage.setItem('fab-position', JSON.stringify(position)); } catch {}
      }
      
      // Only trigger onClick if not moved
      if (!hasMoved.current) {
        onClick();
      }
    }
  };

  const handleClick = (e) => {
    // Prevent click if we just finished dragging
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick();
  };

  const getPositionStyles = () => {
    if (position.x !== null && position.y !== null) {
      return {
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        bottom: 'auto',
        right: 'auto'
      };
    }
    // Default position
    return {};
  };

  // Calculate safe bottom position for iOS
  const safeBottomClass = position.x === null ? 'bottom-[calc(7rem+env(safe-area-inset-bottom,0px))] md:bottom-8' : '';

  return (
    <Button
      ref={buttonRef}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      size="lg"
      aria-label={label}
      className={`fixed ${safeBottomClass} right-4 md:right-6 z-30 rounded-full h-12 w-12 md:h-auto md:w-auto md:rounded-xl shadow-2xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all hover:scale-105 touch-none tap-highlight`}
      style={getPositionStyles()}
      data-testid="floating-action-button"
    >
      <Icon size={18} className="md:mr-2" aria-hidden="true" />
      <span className="hidden md:inline font-semibold">{label}</span>
    </Button>
  );
};

export default FloatingActionButton;